import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, TextArea as SemanticTextArea, Icon } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { ResourceListOfOptionDto } from 'src/services/Client';
import HATEOASClient from 'src/services/HATEOASClient';

export interface CommentSuggestion {
    langCode: string;
    content: string;
    aiGenerated: boolean;
}

interface CommentSuggestionsTextAreaProps {
    value: any;
    onChange: (value: any, aiGenerated?: boolean) => void;
    readOnly: boolean;
    client?: HATEOASClient;
    postOptions?: ResourceListOfOptionDto;
    labelSuggestions?: string;
    labelLoading?: string;
    labelNa?: string;
    labelNone?: string;
    labelPlaceholder?: string;
    labelFailed?: string;
    aiGeneratedComment?: boolean;
    editDialogHeader?: {
        dialogTitle: string;
        optionCode: string;
        editingColumn: string;
        optionType: string;
    };
}

const SUGGESTION_DEBOUNCE_MS = 500;
const SUGGESTION_LIMIT = 3;
const SUGGESTION_MIN_LENGTH = 2;
const NA_SUGGESTION: CommentSuggestion = {
    langCode: 'na',
    content: 'NA',
    aiGenerated: false,
};

const CommentSuggestionsTextArea: React.FC<CommentSuggestionsTextAreaProps> = ({
    value,
    onChange,
    readOnly,
    client,
    postOptions,
    labelSuggestions,
    labelLoading,
    labelNa,
    labelNone,
    labelPlaceholder,
    labelFailed,
    aiGeneratedComment,
    editDialogHeader,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState<string>(value || '');
    const [suggestions, setSuggestions] = useState<CommentSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [closeOnDimmerClick, setCloseOnDimmerClick] = useState(true);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [isAiGenerated, setIsAiGenerated] = useState<boolean>(aiGeneratedComment || false);
    const debounceRef = useRef<any>(null);
    const selectionRef = useRef<any>(null);

    useEffect(
        () => {
            setInputValue(value || '');
        },
        [value]
    );

    useEffect(
        () => {
            const handleMouseRelease = () => setCloseOnDimmerClick(true);
            document.addEventListener('click', handleMouseRelease);
            return () => document.removeEventListener('click', handleMouseRelease);
        },
        []
    );

    const fetchSuggestions = useCallback(
        async (text: string) => {
            if (!client || !postOptions || !text || text.trim().length < SUGGESTION_MIN_LENGTH) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            const canExec = client.GET_optionCommentSuggestions(postOptions).canExecute();
            if (!canExec) {
                setSuggestions([NA_SUGGESTION]);
                setShowSuggestions(true);
                return;
            }

            setLoadingSuggestions(true);
            try {
                const result = await client.GET_optionCommentSuggestions(postOptions).execute({
                    fragment: text,
                    limit: SUGGESTION_LIMIT,
                });
                if (result && result.length > 0) {
                    setSuggestions(result as CommentSuggestion[]);
                    setShowSuggestions(true);
                } else {
                    setSuggestions([NA_SUGGESTION]);
                    setShowSuggestions(true);
                }
            } catch (e) {
                const err = e as any;
                const response = err && err.response ? err.response : null;
                const status = response ? response.status : null;
                const serverMsg = response && response.data ? response.data.message : null;
                const msg = status
                    ? 'Error ' + status + ': ' + (serverMsg || labelFailed)
                    : labelFailed;
                toast.error(msg, { toastId: 'comment-suggestions-error' });
                setSuggestions([NA_SUGGESTION]);
                setShowSuggestions(true);
            } finally {
                setLoadingSuggestions(false);
            }
        },
        [client, postOptions]
    );

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setShowSuggestions(false);
        setDropdownOpen(false);
        setActiveIndex(-1);
        setIsAiGenerated(false);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(
            () => {
                fetchSuggestions(newValue);
            },
            SUGGESTION_DEBOUNCE_MS
        );
    };

    const handleSuggestionSelect = (suggestion: CommentSuggestion, index: number) => {
        setActiveIndex(index);

        if (selectionRef.current) {
            clearTimeout(selectionRef.current);
        }
        selectionRef.current = setTimeout(
            () => {
                setInputValue(suggestion.content);
                setIsAiGenerated(suggestion.aiGenerated);
                setShowSuggestions(false);
                setDropdownOpen(false);
                setSuggestions([]);
                setActiveIndex(-1);
            },
            120
        );
    };

    const handleClose = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        if (selectionRef.current) {
            clearTimeout(selectionRef.current);
        }
        const prevValue = value || '';
        if (prevValue !== inputValue) {
            onChange(inputValue, isAiGenerated);
        }
        setShowSuggestions(false);
        setSuggestions([]);
        setDropdownOpen(false);
        setActiveIndex(-1);
        setModalOpen(false);
    };

    const handleOpen = () => {
        setModalOpen(true);
    };

    const displayValue = inputValue || '';

    return (
        <Modal
            open={modalOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            id="modal-container"
            dimmer="inverted"
            closeOnDimmerClick={closeOnDimmerClick}
            trigger={
                <div className={(isAiGenerated ? 'ai-illuminated-border ' : '') + 'width--100Percent text-area-modal'}>
                    <SemanticTextArea
                        className="width--100Percent text-area-modal"
                        value={displayValue}
                        autoHeight={true}
                        disabled={readOnly}
                    />
                    <div className="disabled_input" />
                </div>}
        >
            {editDialogHeader && (
                <Modal.Header>
                    <div className="display--flex justify-content--space-between">
                        <span>
                            {editDialogHeader.dialogTitle + ' - '}
                            <span style={{ color: '#950014' }}>{editDialogHeader.optionCode}</span>
                            {' - '}
                            <span style={{ color: '#950014' }}>{editDialogHeader.optionType}</span>
                        </span>
                        <span>{editDialogHeader.editingColumn}</span>
                    </div>
                </Modal.Header>
            )}
            <Modal.Content>
                <SemanticTextArea
                    className="width--100Percent"
                    value={displayValue}
                    onChange={handleTextChange as any}
                    id="dialog-textArea-comment"
                    disabled={readOnly}
                    ref={(input: any) => {
                        if (input) {
                            const x = window.scrollX;
                            const y = window.scrollY;
                            input.focus();
                            window.scrollTo(x, y);
                        }
                    }}
                    onFocus={(e: any) => {
                        const val = e.target.value;
                        e.target.value = '';
                        e.target.value = val;
                    }}
                    onMouseDown={() => closeOnDimmerClick && setCloseOnDimmerClick(false)}
                />

                {loadingSuggestions && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 3, color: '#6a6f76', fontSize: 13 }}>
                        <Icon name="spinner" size="large" color="blue" loading={true} />
                        <span>{labelLoading}</span>
                    </div>
                )}

                {showSuggestions && suggestions.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#6a6f76', marginBottom: 4 }}>{labelSuggestions}</div>
                        {(() => {
                            const isNaOnly = suggestions.length === 1 && suggestions[0].langCode === 'na';
                            return (
                        <div style={{ width: '100%', border: '1px solid #c8cdd4', borderRadius: 6, boxSizing: 'border-box' as any }}>
                            <button
                                type="button"
                                disabled={isNaOnly}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 10px',
                                    fontSize: 13,
                                    color: isNaOnly ? '#888' : '#444',
                                    background: isNaOnly ? '#f5f5f5' : '#fff',
                                    border: 'none',
                                    borderRadius: dropdownOpen ? '6px 6px 0 0' : 6,
                                    cursor: isNaOnly ? 'default' : 'pointer',
                                    outline: 'none',
                                    boxSizing: 'border-box' as any,
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => !isNaOnly && setDropdownOpen((prev) => !prev)}
                            >
                                <span>{isNaOnly ? labelNa : labelPlaceholder}</span>
                                {!isNaOnly && (
                                    <span style={{ fontSize: 9, color: '#888', marginLeft: 8 }}>{dropdownOpen ? '▲' : '▼'}</span>
                                )}
                            </button>
                            {isNaOnly && (
                                <div style={{ padding: '4px 10px 6px', fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>
                                    {labelNone}
                                </div>
                            )}
                            {dropdownOpen && (
                                <div
                                    style={{
                                        width: '100%',
                                        background: '#fff',
                                        border: '1px solid #c8cdd4',
                                        borderTop: '1px solid #e8e8e8',
                                        borderRadius: '0 0 6px 6px',
                                        maxHeight: 200,
                                        overflowY: 'auto' as any,
                                        boxSizing: 'border-box' as any,
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                                    }}
                                >
                                    {suggestions.map((suggestion, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                padding: '10px 12px',
                                                fontSize: 13,
                                                color: activeIndex === idx ? '#950014' : '#222',
                                                background: activeIndex === idx ? '#fdf0f2' : '#fff',
                                                cursor: 'pointer',
                                                whiteSpace: 'normal' as any,
                                                wordBreak: 'break-word' as any,
                                                lineHeight: 1.5,
                                                borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none',
                                                userSelect: 'none' as any,
                                            }}
                                            onMouseEnter={() => setActiveIndex(idx)}
                                            onMouseLeave={() => setActiveIndex(-1)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSuggestionSelect(suggestion, idx)}
                                        >
                                            {suggestion.content}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                            );
                        })()}
                    </div>
                )}
            </Modal.Content>
        </Modal>
    );
};

export default CommentSuggestionsTextArea;



// WEBPACK FOOTER //
// ./src/components/Table/components/basic/CommentSuggestionsTextArea.tsx